import { memo, PropsWithChildren, useMemo } from 'react'

import { ForwardToInbox, Groups, Person } from '@mui/icons-material'
import { Box } from '@mui/material'
import { useMatchRoute, useNavigate } from '@tanstack/react-router'

import { Tab } from '@shared/components/MaterialUIEnhancements/Tabs'
import { StatelessTabs } from '@shared/components/MaterialUIEnhancements/Tabs/StatelessTabs'
import { StaticRouteParameter } from 'config/routing'
import {
  invitationsOverviewRoute,
  membersRoute,
  teamsOverviewRoute,
  usersOverviewRoute,
} from 'pages/NWPlatform/Organizations/Organization/Members'

type IconTabProps = {
  id: string
}

const IconTab = memo(function IconTabRaw({ id, children }: PropsWithChildren<IconTabProps>) {
  return (
    <Tab
      sx={{
        fontSize: 20,
        width: 80,
      }}
      id={id}
      ripple
    >
      {children}
    </Tab>
  )
})

const ContentTabsRaw = () => {
  const navigate = useNavigate({ from: membersRoute.fullPath })
  const matchRoute = useMatchRoute()

  const userPath = matchRoute({ to: usersOverviewRoute.fullPath })
  const teamPath = matchRoute({ to: teamsOverviewRoute.fullPath })
  const invitationPath = matchRoute({ to: invitationsOverviewRoute.fullPath })

  const paths = useMemo(
    () => ({
      [StaticRouteParameter.USER]: usersOverviewRoute.to,
      [StaticRouteParameter.TEAMS]: teamsOverviewRoute.to,
      [StaticRouteParameter.INVITATIONS]: invitationsOverviewRoute.to,
    }),
    [],
  )

  const derivedActiveTab = userPath
    ? StaticRouteParameter.USER
    : teamPath
      ? StaticRouteParameter.TEAMS
      : invitationPath
        ? StaticRouteParameter.INVITATIONS
        : undefined

  return (
    <Box sx={{ mb: 1, width: 'min-content' }}>
      <StatelessTabs
        variant="flat"
        value={derivedActiveTab}
        setActiveTab={(id) => {
          if (id) navigate({ to: paths[id as keyof typeof paths] })
        }}
      >
        <IconTab id={StaticRouteParameter.USER}>
          <Person fontSize="inherit" />
        </IconTab>
        <IconTab id={StaticRouteParameter.TEAMS}>
          <Groups fontSize="inherit" />
        </IconTab>
        <IconTab id={StaticRouteParameter.INVITATIONS}>
          <ForwardToInbox fontSize="inherit" />
        </IconTab>
      </StatelessTabs>
    </Box>
  )
}

export const ContentTabs = memo(ContentTabsRaw)
